import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/contexts/ToastContext';
import {
  getLastUndoRedoSessionId,
  getSessionUndoRedoSnapshot,
  redoLastSessionChange,
  refreshSessionUndoRedoAvailability,
  setLastUndoRedoSessionId,
  subscribeSessionUndoRedoStore,
  undoLastSessionChange,
} from '@/features/session-change-history/sessionUndoRedoStore';

export {
  getLastUndoRedoSessionId,
  setLastUndoRedoSessionId,
} from '@/features/session-change-history/sessionUndoRedoStore';

export function useSessionUndoRedo(sessionId: string | null | undefined) {
  const queryClient = useQueryClient();
  const { showError, showSuccess } = useToast();
  const snapshot = useSyncExternalStore(
    subscribeSessionUndoRedoStore,
    getSessionUndoRedoSnapshot,
    getSessionUndoRedoSnapshot
  );
  const effectiveSessionId = sessionId ?? snapshot.sessionId ?? getLastUndoRedoSessionId();

  useEffect(() => {
    if (sessionId) {
      setLastUndoRedoSessionId(sessionId);
      void refreshSessionUndoRedoAvailability(queryClient, sessionId).catch(() => {});
      return;
    }
    const current = getLastUndoRedoSessionId();
    if (current) {
      void refreshSessionUndoRedoAvailability(queryClient, current).catch(() => {});
    }
  }, [effectiveSessionId, sessionId, queryClient]);

  const undo = useCallback(() => {
    void undoLastSessionChange(queryClient, effectiveSessionId)
      .then((res) => {
        if (!res.ok) {
          if (res.error_code === 'non_reversible') {
            showError(res.message ?? 'Невозможно отменить удаление сессии');
          } else {
            showError(res.message ?? 'Нет изменений для отмены');
          }
          return;
        }
        showSuccess('Изменение отменено');
      })
      .catch((e: Error) => showError(e.message));
  }, [effectiveSessionId, queryClient, showError, showSuccess]);

  const redo = useCallback(() => {
    void redoLastSessionChange(queryClient, effectiveSessionId)
      .then((res) => {
        if (!res.ok) {
          showError(res.message ?? 'Нет изменений для повтора');
          return;
        }
        showSuccess('Изменение повторено');
      })
      .catch((e: Error) => showError(e.message));
  }, [effectiveSessionId, queryClient, showError, showSuccess]);

  return {
    canUndo: snapshot.canUndo,
    canRedo: snapshot.canRedo,
    undoPreview: snapshot.undoPreview,
    redoPreview: snapshot.redoPreview,
    undo,
    redo,
    isUndoing: snapshot.isUndoing,
    isRedoing: snapshot.isRedoing,
    effectiveSessionId,
  };
}

/** Физическая клавиша Z — не зависит от раскладки (на RU e.key === 'я'). */
function isUndoRedoShortcutKey(e: KeyboardEvent): boolean {
  return e.code === 'KeyZ' || e.key === 'z' || e.key === 'Z';
}

function isTextEditingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName.toLowerCase();
  return (
    tag === 'input' ||
    tag === 'textarea' ||
    tag === 'select' ||
    target.isContentEditable
  );
}

export function useSessionUndoRedoShortcuts(
  opts: {
    enabled?: boolean;
    sessionId?: string | null;
  } = {}
) {
  const queryClient = useQueryClient();
  const { showError, showSuccess } = useToast();
  const enabled = opts.enabled ?? true;
  const sessionId = opts.sessionId;

  useEffect(() => {
    if (!enabled) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || e.altKey) return;
      if (!isUndoRedoShortcutKey(e)) return;
      if (isTextEditingTarget(e.target)) return;

      e.preventDefault();
      // stopPropagation so the global project undo hook doesn't catch it
      e.stopPropagation();

      const effectiveId = sessionId ?? getLastUndoRedoSessionId();
      if (e.shiftKey) {
        void redoLastSessionChange(queryClient, effectiveId)
          .then((res) => {
            if (!res.ok) {
              showError(res.message ?? 'Нет изменений для повтора');
              return;
            }
            showSuccess('Изменение повторено');
          })
          .catch((err: Error) => showError(err.message));
        return;
      }
      void undoLastSessionChange(queryClient, effectiveId)
        .then((res) => {
          if (!res.ok) {
            if (res.error_code === 'non_reversible') {
              showError(res.message ?? 'Невозможно отменить удаление сессии');
            } else {
              showError(res.message ?? 'Нет изменений для отмены');
            }
            return;
          }
          showSuccess('Изменение отменено');
        })
        .catch((err: Error) => showError(err.message));
    };
    
    // Use capture phase so we intercept before standard document handlers
    window.addEventListener('keydown', onKeyDown, { capture: true });
    return () => window.removeEventListener('keydown', onKeyDown, { capture: true });
  }, [enabled, sessionId, queryClient, showError, showSuccess]);
}
